import {
  $isListNode,
  ListItemNode,
  type SerializedListItemNode,
} from '@lexical/list';
import type {
  DOMConversionFn,
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
} from 'lexical';

import { $isCustomListNode } from './CustomListNode';

/**
 * Type for HTML list item element with custom string value property
 * Note: HTMLLIElement.value is a number (for ordered lists), so we use a custom property
 */
type ListItemElementWithStringValue = HTMLElement & {
  value?: string | number;
};

/**
 * Helper function to create DOM elements with attributes
 */
function createElement(
  tag: string,
  attrs: Record<string, string | null> = {},
  children: (string | HTMLElement)[] = [],
): HTMLElement {
  const element = document.createElement(tag);

  // Set attributes
  for (const [key, value] of Object.entries(attrs)) {
    if (value !== null) {
      element.setAttribute(key, value);
    }
  }

  // Append children
  for (const child of children) {
    element.append(
      typeof child === 'string' ? document.createTextNode(child) : child,
    );
  }

  return element;
}

/**
 * Updates the checked state and task list styling for a list item DOM element
 */
function updateListItemChecked(
  dom: HTMLElement,
  listItemNode: ListItemNode,
): void {
  // Only set task list attrs for leaf list items
  const shouldBeTaskItem = !$isListNode(listItemNode.getFirstChild());
  dom.classList.toggle('task-list-item', shouldBeTaskItem);

  if (listItemNode.__checked) {
    dom.setAttribute('checked', 'checked');
  } else {
    dom.removeAttribute('checked');
  }
}

/**
 * Checks if the list item's parent is a checked list
 */
function isCheckedListItem(node: CustomListItemNode): boolean {
  const parent = node.getParent();
  return $isListNode(parent) && parent.getListType() === 'check';
}

export class CustomListItemNode extends ListItemNode {
  static getType(): string {
    return 'custom-list-item';
  }

  static clone(node: CustomListItemNode): CustomListItemNode {
    return new CustomListItemNode(node.__value, node.__checked, node.__key);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createDOM(_config: EditorConfig): HTMLElement {
    const element = document.createElement(
      'li',
    ) as ListItemElementWithStringValue;

    if (isCheckedListItem(this)) {
      updateListItemChecked(element, this);
    }

    // Set custom value property (HTMLLIElement.value is number, we use string)
    element.value = this.__value as string | number;

    if ($hasNestedListWithoutLabel(this)) {
      element.style.listStyle = 'none';
    }

    return element;
  }

  updateDOM(
    _prevNode: ListItemNode,
    dom: HTMLElement,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _config: EditorConfig,
  ): boolean {
    if (isCheckedListItem(this)) {
      updateListItemChecked(dom, this);
    }

    (dom as ListItemElementWithStringValue).value = this.__value as
      | string
      | number;

    return false;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = this.createDOM(editor._config);
    element.style.textAlign = this.getFormatType();

    if (element.classList.contains('task-list-item')) {
      const input = createElement('input', {
        type: 'checkbox',
        disabled: 'disabled',
      });

      if (element.hasAttribute('checked')) {
        input.setAttribute('checked', 'checked');
        element.removeAttribute('checked');
      }

      element.prepend(input);
    }

    return {
      element,
    };
  }

  static importDOM(): DOMConversionMap | null {
    // @ts-expect-error - accessing protected method
    const converter = super.importDOM()?.li?.()?.conversion as
      | DOMConversionFn<HTMLElement>
      | undefined;

    if (!converter) {
      return null;
    }

    const customConvertFunction = (element: HTMLElement) => {
      const baseResult = converter(element);

      if (!baseResult?.node) {
        return baseResult;
      }

      const node = baseResult.node as CustomListItemNode;
      const listElement = element as ListItemElementWithStringValue;

      // Extract value attribute from the li element
      // HTMLLIElement.value is number, but we store as string in __value
      if (listElement.value !== undefined && listElement.value !== null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node as any).__value = String(listElement.value);
      }

      // Check for checkbox input (task list item)
      // The checkbox is prepended in exportDOM, so check first child first
      const firstChild = element.firstElementChild;
      const checkbox =
        firstChild instanceof HTMLInputElement && firstChild.type === 'checkbox'
          ? firstChild
          : element.querySelector<HTMLInputElement>('input[type="checkbox"]');

      if (checkbox) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node as any).__checked = checkbox.hasAttribute('checked');
      }

      return baseResult;
    };

    return {
      li: () => ({
        conversion: customConvertFunction,
        priority: 0,
      }),
    };
  }

  exportJSON(): SerializedListItemNode {
    return {
      ...super.exportJSON(),
      type: 'custom-list-item',
    };
  }
}

function $hasNestedListWithoutLabel(node: CustomListItemNode): boolean {
  const children = node.getChildren();
  let hasLabel = false;
  let hasNestedList = false;

  for (const child of children) {
    if ($isCustomListNode(child)) {
      hasNestedList = true;
    } else if (child.getTextContent().trim().length > 0) {
      hasLabel = true;
    }
  }

  return hasNestedList && !hasLabel;
}

export function $isCustomListItemNode(
  node: LexicalNode | null | undefined,
): node is CustomListItemNode {
  return node instanceof CustomListItemNode;
}

export function $createCustomListItemNode(): CustomListItemNode {
  return new CustomListItemNode();
}
